import { ExternalConnectionsProviders } from '@griply/types';

import { MONTH_ORDINALS } from './schedules/constants';

export type ProviderRecurrenceLimits = {
  monthOrdinals: readonly number[];
  allowsMultipleMonthDays: boolean;
};

// Microsoft Graph has no RRULE parser — it reads six structured patterns — so a rule Griply can express
// may have nowhere to go. Its `index` runs first…fourth plus last (no fifth), and its monthly patterns
// produce exactly one occurrence per month, so "the 11th and the 15th" cannot be one Outlook series.
// The backend refuses those writes rather than silently degrading them to a one-off, which is why the
// picker has to stop offering them in the first place.
const OUTLOOK_MONTH_ORDINALS: readonly number[] = MONTH_ORDINALS.filter(
  (ordinal) => ordinal !== 5,
);

const UNLIMITED: ProviderRecurrenceLimits = {
  monthOrdinals: MONTH_ORDINALS,
  allowsMultipleMonthDays: true,
};

const OUTLOOK: ProviderRecurrenceLimits = {
  monthOrdinals: OUTLOOK_MONTH_ORDINALS,
  allowsMultipleMonthDays: false,
};

// `undefined` (no target calendar picked yet, or a habit/task schedule, which no provider stores) keeps
// every option — the constraint belongs to the provider, not to recurrence editing as such.
export const getProviderRecurrenceLimits = (
  provider?: ExternalConnectionsProviders | null,
): ProviderRecurrenceLimits =>
  provider === ExternalConnectionsProviders.Outlook ? OUTLOOK : UNLIMITED;

// What the month-day grid holds after tapping `day`. Where several days cannot travel, the grid behaves
// as a single choice — picking a second day replaces the first instead of building a rule the provider
// will reject on save. Tapping the selected day still clears it, same as the multi-select grid.
export const getNextSelectedMonthDays = (
  selectedDays: number[],
  day: number,
  limits: ProviderRecurrenceLimits,
): number[] => {
  const without = selectedDays.filter((value) => value !== day);
  if (without.length !== selectedDays.length) {
    return without;
  }

  return limits.allowsMultipleMonthDays ? [...selectedDays, day] : [day];
};
